import { DataGrid, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useState } from "react";
import { InstanceTypeInfo, SpotPrice } from "@aws-sdk/client-ec2";
import { Filter, FilterObject, getFilterInitialState } from "./Filter";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import { Box, IconButton, Link } from "@mui/material";
import { getSpotInstanceTableData } from "./getSpotInstanceTableData";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import RefreshIcon from "@mui/icons-material/Refresh";
import { clearLocalStorage } from "../../utils/localStorage";

interface AugmentedInstanceInfo extends InstanceTypeInfo {
  SpotInstancePrices: SpotPrice[];
}

export const SpotInstanceTable = () => {
  const [filter, setFilter] = useState(getFilterInitialState());
  const [isLoading, setIsLoading] = useState(false);
  const [instanceData, setInstanceData] = useState<AugmentedInstanceInfo[]>([]);

  const fetchData = useCallback((filter: FilterObject) => {
    setIsLoading(true);

    setTimeout(() => {
      getSpotInstanceTableData(filter).then((instancesWithSpotPrice) => {
        setInstanceData(instancesWithSpotPrice);

        setIsLoading(false);
      });
    }, 10);
  }, []);

  const handleReset = useCallback(() => {
    setFilter(getFilterInitialState());
  }, []);

  const handleRefresh = useCallback(() => {
    clearLocalStorage();
    fetchData(filter);
  }, [fetchData, filter]);

  const handleFilterUpdate = useCallback((filter: FilterObject) => {
    setFilter(filter);
  }, []);

  useEffect(() => {
    fetchData(filter);
  }, [fetchData, filter]);

  useEffect(() => {
    fetchData(getFilterInitialState());
  }, [fetchData]);

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb">
        <Link underline="hover" color="inherit" href="/">
          Spot Instances
        </Link>
      </Breadcrumbs>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "row",
          gap: "20px",
        }}
      >
        <Filter
          filter={filter}
          onUpdate={handleFilterUpdate}
          onReset={handleReset}
          disabled={isLoading}
        />
        <Card sx={{ flex: 1, width: "100%", height: "100%" }}>
          <CardHeader
            title="Spot Instances"
            action={
              <IconButton disabled={isLoading} onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            }
          />
          <CardContent sx={{ height: "calc(100% - 100px)" }}>
            <DataGrid
              sx={{
                ".MuiDataGrid-columnHeader:focus,.MuiDataGrid-columnHeader:focus-within,.MuiDataGrid-cell:focus,.MuiDataGrid-cell:focus-within":
                  {
                    outline: "none",
                  },
              }}
              loading={isLoading}
              paginationMode="client"
              rowCount={instanceData.length}
              rowHeight={40}
              disableColumnFilter={true}
              disableColumnSelector={true}
              disableColumnMenu={true}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 50 },
                },
                sorting: {
                  sortModel: [{ field: "SpotInstancePrices", sort: "asc" }],
                },
              }}
              columns={[
                { field: "InstanceType", headerName: "Instance Type", flex: 1 },
                {
                  field: "vCPUs",
                  headerName: "vCPUs",
                  type: "number",
                  flex: 0.5,
                  align: "left",
                  headerAlign: "left",
                  valueGetter: (instanceType) =>
                    instanceType.row?.VCpuInfo?.DefaultVCpus,
                },
                {
                  field: "MemoryGB",
                  headerName: "Memory",
                  type: "number",
                  flex: 1,
                  align: "left",
                  headerAlign: "left",
                  valueGetter: (instanceType) =>
                    Number(instanceType.row?.MemoryInfo?.SizeInMiB) / 1024,
                  valueFormatter: (data) => `${data.value}GB`,
                },
                {
                  field: "SpotInstancePrices",
                  headerName: "Cheapest price",
                  type: "number",
                  flex: 1,
                  valueGetter: (data) =>
                    data.row?.SpotInstancePrices[0].SpotPrice,
                  renderCell: (params: GridRenderCellParams) => (
                    <Link href={params.row?.InstanceType} target="_blank">
                      ${params.value}
                    </Link>
                  ),
                },

                {
                  field: "cheapestSpotInstanceAZ",
                  headerName: "Availability Zone",
                  align: "right",
                  headerAlign: "right",
                  valueGetter: (data) =>
                    data.row?.SpotInstancePrices[0].AvailabilityZone,
                  flex: 1,
                },
                {
                  field: "Region",
                  headerName: "Region",
                  align: "right",
                  headerAlign: "right",
                  flex: 1,
                  valueGetter: (instanceType) =>
                    instanceType.row?.SpotInstancePrices[0].AvailabilityZone?.slice(
                      0,
                      -1,
                    ),
                },
              ]}
              rows={instanceData.map((instanceType) => ({
                id: instanceType.InstanceType,
                ...instanceType,
              }))}
            />
          </CardContent>
        </Card>
      </Box>
    </>
  );
};
